import { useOctaneContext } from 'contexts/octaneContext';
import {
  Actions,
  SchemaTypes,
  useActiveSubscription,
  useMeters,
} from 'octane-components';
import { useEffect, useMemo, useState } from 'react';
import {
  ALL_APP_IDS,
  ALL_FEATURE_IDS,
  ALL_VIEW_IDS,
} from 'constants/appConstants';
import _ from 'lodash';
import { Entitlement } from 'types/access';

declare type CustomerPortalUsage = SchemaTypes['CustomerPortalUsage'];

const entitlementsToMeters: {
  [key: string]: string;
} = {
  WATCHLIST: 'part_watchlist_subscription',
  QUOTES: 'part_quote_created',
};

export default function useOctaneHelpers() {
  const { octaneActiveToken, octaneParentToken } = useOctaneContext();
  const { getCustomerUsage } = Actions;
  const {
    loading: loadingOctaneActiveOrgSubscription,
    result: octaneActiveOrgSubscription,
  } = useActiveSubscription({ token: octaneActiveToken });
  const {
    loading: loadingOctaneParentOrgSubscription,
    result: octaneParentOrgSubscription,
  } = useActiveSubscription({ token: octaneParentToken });
  const { loading: loadingOctaneMeters, result: octaneMeters } = useMeters({
    token: octaneActiveToken,
  });
  const [octaneUsage, setOctaneUsage] = useState<CustomerPortalUsage[]>([]);
  const [octaneUsageLoading, setOctaneUsageLoading] = useState(false);

  useEffect(() => {
    if (octaneMeters && octaneActiveToken) {
      setOctaneUsageLoading(true);
      Promise.all(
        octaneMeters.map((meter) =>
          getCustomerUsage(octaneActiveToken, {
            meter_name: meter.meter_name || '',
          })
        )
      ).then((usage) => {
        setOctaneUsage((usage as CustomerPortalUsage[]) || []);
        setOctaneUsageLoading(false);
      });
    }
  }, [octaneMeters, octaneActiveToken]);

  const octaneEntitlements = useMemo(() => {
    const entitlements: Entitlement[] = [];

    // first get all non-warehouse entitlements from octaneActiveOrgSubscription
    _.filter(
      octaneActiveOrgSubscription?.subscription?.price_plan?.features || [],
      (f) => !_.includes(f.name, 'warehouse')
    ).forEach((f) => {
      entitlements.push({
        id: f.name.toUpperCase() as
          | keyof typeof ALL_FEATURE_IDS
          | ALL_APP_IDS
          | ALL_VIEW_IDS,
      });
    });
    entitlements.forEach((e) => {
      // check if there is a meter associated with this entitlement
      if (_.get(entitlementsToMeters, e.id, null)) {
        // get the associated meter
        const meter = _.chain(
          octaneActiveOrgSubscription?.subscription?.price_plan
            ?.metered_components || []
        )
          .find({ meter_name: _.get(entitlementsToMeters, e.id, '') })
          .value();
        _.set(
          e,
          'planLimit',
          _.first(meter.label_limits.slice(-1))?.limit || null
        );
        _.set(
          e,
          'planIncluded',
          _.first(meter.price_scheme.prices)?.cap || null
        );
      }
    });

    // then get warehouse entitlements from octaneParentOrgSubscription
    _.filter(
      octaneParentOrgSubscription?.subscription?.price_plan?.features || [],
      (f) => _.includes(f.name, 'warehouse')
    ).forEach((f) => {
      entitlements.push({
        id: f.name.toUpperCase() as
          | keyof typeof ALL_FEATURE_IDS
          | ALL_APP_IDS
          | ALL_VIEW_IDS,
      });
    });
    // check add ons for warehouse too
    // ts-ignore is due to issue with octane types
    if (
      _.chain(octaneParentOrgSubscription?.subscription?.add_ons || [])
        .map('add_on')
        .map('feature') // @ts-ignore
        .some((f) => _.includes(f.name, 'warehouse_app'))
        .value()
    ) {
      entitlements.push({
        id: ALL_APP_IDS.WAREHOUSE,
      });
    }

    // then update access based on octaneUsage
    entitlements.forEach((e) => {
      if (_.get(entitlementsToMeters, e.id, null)) {
        const usage = _.chain(octaneUsage || [])
          .find({
            meter_name: _.get(entitlementsToMeters, e.id, ''),
          })
          .get(['current_cycle_usage', 'total_usage'])
          .value();
        _.set(e, 'usage', usage);
        _.set(e, 'isOverPlanLimit', usage >= (e.planLimit || Infinity));
        _.set(e, 'isOverPlanIncluded', usage >= (e.planIncluded || 0));
      }
    });

    return entitlements;
  }, [
    octaneActiveOrgSubscription,
    octaneParentOrgSubscription,
    octaneUsage,
    octaneUsageLoading,
    loadingOctaneActiveOrgSubscription,
    loadingOctaneParentOrgSubscription,
  ]);

  return {
    loading:
      loadingOctaneParentOrgSubscription ||
      loadingOctaneActiveOrgSubscription ||
      loadingOctaneMeters ||
      octaneUsageLoading,
    octaneActiveOrgSubscription,
    octaneMeters,
    octaneUsage,
    octaneEntitlements,
  };
}
